import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { notificationsAPI } from '../../api/notifications'
import { FiBell, FiLogOut, FiUser, FiCheck, FiTrash2, FiCheckCircle, FiInbox, FiMenu } from 'react-icons/fi'
import { toast } from 'react-hot-toast'

const NotificationDropdown = ({ onClose }) => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const ref = useRef(null)

  useEffect(() => {
    fetchNotifications()
  }, [])

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const [notifRes, countRes] = await Promise.all([
        notificationsAPI.getNotifications({ page: 0, size: 10 }),
        notificationsAPI.getUnreadCount()
      ])
      setNotifications(notifRes.data?.content || notifRes.data || [])
      setUnreadCount(countRes.data?.count ?? countRes.data ?? 0)
    } catch {
      // silently fail — don't toast for background fetches
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = async (e, id) => {
    e.stopPropagation()
    try {
      await notificationsAPI.markAsRead(id)
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch {
      toast.error('Failed to mark as read')
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead()
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
      toast.success('All notifications marked as read')
    } catch {
      toast.error('Failed to mark all as read')
    }
  }

  const handleDelete = async (e, id) => {
    e.stopPropagation()
    try {
      await notificationsAPI.deleteNotification(id)
      const deleted = notifications.find(n => n.id === id)
      setNotifications(prev => prev.filter(n => n.id !== id))
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch {
      toast.error('Failed to delete notification')
    }
  }

  const formatTime = (date) => {
    if (!date) return ''
    const d = new Date(date)
    const now = new Date()
    const diff = Math.floor((now - d) / 1000)
    if (diff < 60) return 'Just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return d.toLocaleDateString()
  }

  const getTypeStyle = (type) => {
    const styles = {
      ORDER: 'bg-orange-100 text-[#ff6900]',
      PAYMENT: 'bg-green-100 text-green-700',
      PROMO: 'bg-amber-100 text-amber-700',
      SYSTEM: 'bg-gray-100 text-gray-700',
      ALERT: 'bg-red-100 text-red-700',
    }
    return styles[type?.toUpperCase()] || styles.SYSTEM
  }

  return (
    <div
      ref={ref}
      className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">Notifications</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-bold bg-[#ff6900] text-white rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="flex items-center gap-1 text-xs text-[#ff6900] hover:text-[#e05d00] font-medium transition"
          >
            <FiCheckCircle size={12} /> Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
              <FiInbox size={20} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-600 font-medium">No notifications</p>
            <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
          </div>
        ) : (
          notifications.map((notif) => (
            <div
              key={notif.id}
              className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition group ${!notif.read ? 'bg-orange-50' : ''
                }`}
            >
              {/* Type dot */}
              <div className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${getTypeStyle(notif.type)}`}>
                {(notif.type || 'S').charAt(0).toUpperCase()}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-xs leading-relaxed ${!notif.read ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                  {notif.message || notif.title || notif.content}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{formatTime(notif.createdAt)}</p>
              </div>

              {/* Actions */}
              <div className="flex-shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                {!notif.read && (
                  <button
                    onClick={(e) => handleMarkAsRead(e, notif.id)}
                    className="p-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition"
                    title="Mark as read"
                  >
                    <FiCheck size={13} />
                  </button>
                )}
                <button
                  onClick={(e) => handleDelete(e, notif.id)}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                  title="Delete"
                >
                  <FiTrash2 size={13} />
                </button>
              </div>

              {/* Unread indicator */}
              {!notif.read && (
                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#ff6900] mt-1.5" />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <Link
            to="/admin/notifications"
            onClick={onClose}
            className="block w-full text-xs text-center text-orange-600 hover:text-orange-700 font-medium transition"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  )
}

const AdminHeader = ({ toggleSidebar }) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const profileRef = useRef(null)

  // Fetch unread count for the bell badge
  useEffect(() => {
    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 60000) // refresh every minute
    return () => clearInterval(interval)
  }, [])

  const fetchUnreadCount = async () => {
    try {
      const res = await notificationsAPI.getUnreadCount()
      setUnreadCount(res.data?.count ?? res.data ?? 0)
    } catch {
      // ignore
    }
  }

  // Close profile menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const initials = user?.firstName?.charAt(0) || 'A'

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
      <div className="flex items-center justify-between px-6 py-3 gap-2">

        {/* Mobile menu toggle */}
        <button
          onClick={toggleSidebar}
          className="lg:hidden p-2 rounded-xl text-gray-600 hover:text-[#ff6900] hover:bg-orange-50 transition"
        >
          <FiMenu size={22} />
        </button>

        <div className="flex items-center gap-2 ml-auto">

          {/* Notifications Bell */}
          <div className="relative">
            <button
              onClick={() => { setShowNotifications(v => !v); setShowProfileMenu(false) }}
              className="relative p-2 rounded-xl text-gray-600 hover:text-[#ff6900] hover:bg-orange-50 transition"
            >
              <FiBell size={19} />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#ff6900] rounded-full ring-2 ring-white" />
              )}
            </button>

            {showNotifications && (
              <NotificationDropdown onClose={() => setShowNotifications(false)} />
            )}
          </div>

          {/* Profile */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => { setShowProfileMenu(v => !v); setShowNotifications(false) }}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-orange-50 transition"
            >
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.firstName}
                  className="w-7 h-7 rounded-full object-cover ring-2 ring-gray-200" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#ff6900] to-[#e05d00] text-white flex items-center justify-center text-xs font-bold">
                  {initials}
                </div>
              )}
              <span className="hidden md:block text-sm font-medium text-gray-700">
                {user?.firstName} {user?.lastName}
              </span>
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 overflow-hidden">
                <Link to="/profile"
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:text-[#ff6900] hover:bg-orange-50 transition"
                  onClick={() => setShowProfileMenu(false)}>
                  <FiUser size={15} /> Profile
                </Link>
                <div className="my-1 border-t border-gray-100" />
                <button
                  onClick={() => { setShowProfileMenu(false); logout(); navigate('/login') }}
                  className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 transition">
                  <FiLogOut size={15} /> Logout
                </button>
              </div>
            )}
          </div>

        </div>

      </div>
    </header>
  )
}

export default AdminHeader